import { useEffect, useState } from 'react'
import { getPlatformMetrics, getHistoricalMetrics } from '../services/api'
import ComplianceScore from '../components/ComplianceScore'
import MetricsChart from '../components/MetricsChart'
import AppStatusGrid from '../components/AppStatusGrid'
import RecentViolations from '../components/RecentViolations'
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle } from 'lucide-react'

export default function Dashboard() {
  const [metrics, setMetrics] = useState<any>(null)
  const [historicalData, setHistoricalData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      try {
        const [platformMetrics, historical] = await Promise.all([
          getPlatformMetrics(),
          getHistoricalMetrics()
        ])
        setMetrics(platformMetrics)
        setHistoricalData(historical)
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dii-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Governance Dashboard</h2>
        <p className="mt-1 text-sm text-gray-500">
          Real-time FMS Contract V4.1.0 compliance monitoring across all DII applications
        </p>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Platform Score</p>
              <p className="text-3xl font-bold text-gray-900">
                {metrics?.platform_score ? `${(metrics.platform_score * 100).toFixed(0)}%` : '—'}
              </p>
            </div>
            {metrics?.trend === 'improving' ? (
              <TrendingUp className="h-8 w-8 text-green-500" />
            ) : (
              <TrendingDown className="h-8 w-8 text-red-500" />
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {metrics?.trend === 'improving' ? '+2.3%' : '-1.5%'} from last week
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Scans Today</p>
              <p className="text-3xl font-bold text-gray-900">
                {metrics?.total_scans_today || 0}
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-blue-500" />
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Across {metrics?.apps_monitored?.length || 0} applications
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Violations Caught</p>
              <p className="text-3xl font-bold text-red-600">
                {metrics?.violations_caught || 0}
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {metrics?.auto_fixes_available || 0} auto-fixable
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Auto-Fixes Applied</p>
              <p className="text-3xl font-bold text-green-600">
                {metrics?.auto_fixes_applied || 0}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
          <p className="mt-2 text-xs text-gray-500">
            57% fix success rate
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance Score Visualization */}
        <ComplianceScore score={metrics?.platform_score || 0} />

        {/* App Status Grid */}
        <AppStatusGrid apps={metrics?.apps_monitored || []} />
      </div>

      {/* Historical Metrics Chart */}
      <MetricsChart data={historicalData} />

      {/* Recent Violations */}
      <RecentViolations />
    </div>
  )
}